import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { DOCUMENT } from '../../constants/documentTheme';
import { partnerLinkSx, partnerOxbloodBtnSx } from '../PartnerListChrome';
import { formatMoney } from '../../utils/partnerFinance';
import { isHttpsUrl } from '../../utils/groupBilling';
import { CollectionMethodTiles, eyebrowSx, fieldSx } from './BillingParts';

/**
 * mode: 'issue' (first invoice), 'reissue' (void and replace), 'adjustment'
 * (credit note or extra invoice for what changed).
 */
const GroupSendInvoiceDialog = ({
  open,
  onClose,
  masterTripId,
  account,
  invoiceId = null,
  mode = 'issue',
  settings,
  currency = 'USD',
  onDone,
}) => {
  const { t } = useTranslation();
  const [method, setMethod] = useState('in_app');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [offline, setOffline] = useState('');
  const [applyToTrip, setApplyToTrip] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const base = `/partners/trips/${masterTripId}/billing`;

  useEffect(() => {
    if (!open || !account) return;
    const eff = account.effectiveCollection || {};
    setMethod(eff.method || settings?.defaultCollectionMethod || 'in_app');
    setLinkUrl(eff.url || settings?.defaultPaymentLinkUrl || '');
    setLinkLabel(eff.label || settings?.defaultPaymentLinkLabel || '');
    setOffline(eff.offlineInstructions || settings?.offlineInstructions || '');
    setApplyToTrip(false);
    setSendEmail(true);
    setError('');
    setDraft(null);
  }, [open, account, settings]);

  const loadPreview = useCallback(async () => {
    if (!account || mode === 'adjustment') return;
    setLoading(true);
    try {
      const { data } = await api.post(`${base}/accounts/${account.id}/invoice-preview`, {}, { usePartnerAuth: true });
      setDraft(data.draft || null);
    } catch (err) {
      setError(err.response?.data?.error || t('groupBilling.errors.preview'));
    } finally {
      setLoading(false);
    }
  }, [account, base, mode, t]);

  useEffect(() => {
    if (open) loadPreview();
  }, [open, loadPreview]);

  const linkInvalid = method === 'external_link' && !isHttpsUrl(linkUrl);

  const submit = async () => {
    setSaving(true);
    setError('');
    const body = {
      collectionMethod: method,
      paymentLinkUrl: method === 'external_link' ? linkUrl.trim() : null,
      paymentLinkLabel: method === 'external_link' ? (linkLabel.trim() || null) : null,
      offlineInstructions: method === 'offline' ? offline : null,
      applyLinkToTrip: method === 'external_link' && applyToTrip,
      send: sendEmail,
    };
    try {
      let url = `${base}/accounts/${account.id}/invoices`;
      if (mode === 'reissue') url = `${base}/invoices/${invoiceId}/reissue`;
      if (mode === 'adjustment') url = `${base}/accounts/${account.id}/adjustments`;
      const { data } = await api.post(url, body, { usePartnerAuth: true });
      onDone?.(data);
    } catch (err) {
      setError(err.response?.data?.error || t('groupBilling.errors.issue'));
    } finally {
      setSaving(false);
    }
  };

  if (!account) return null;
  const delta = Number(account.invoiceDelta) || 0;
  const titleKey = { issue: 'sendTitle', reissue: 'reissueTitle', adjustment: 'adjustTitle' }[mode];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="group-send-invoice-dialog">
      <DialogTitle sx={{ fontFamily: DOCUMENT.display, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
        {t(`groupBilling.invoice.${titleKey}`, { name: account.name })}
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: 3 }}>
          <Box>
            {mode === 'adjustment' ? (
              <Alert severity={delta < 0 ? 'info' : 'warning'} data-testid="group-adjust-summary">
                {delta < 0
                  ? t('groupBilling.invoice.adjustCredit', { amount: formatMoney(Math.abs(delta), currency) })
                  : t('groupBilling.invoice.adjustExtra', { amount: formatMoney(delta, currency) })}
              </Alert>
            ) : (
              <Box sx={{ border: `1px solid ${DOCUMENT.ruleHard}`, p: 2, bgcolor: '#fff' }} data-testid="group-invoice-preview">
                {loading && <CircularProgress size={22} />}
                {draft && (
                  <>
                    <Typography sx={eyebrowSx}>{t('groupBilling.invoice.billTo')}</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{draft.account?.name}</Typography>
                    {draft.travelers?.length ? (
                      <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mb: 1.5 }}>
                        {t('groupBilling.invoice.travelers', { names: draft.travelers.join(', ') })}
                      </Typography>
                    ) : null}
                    <Stack sx={{ borderTop: `1px solid ${DOCUMENT.rule}` }}>
                      {draft.lines.map((line, i) => (
                        <Box key={`${line.chargeId}-${line.travelerId}-${i}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: `1px solid ${DOCUMENT.rule}` }}>
                          <Typography sx={{ fontSize: 13 }}>{line.description}</Typography>
                          <Typography sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{formatMoney(line.amount, currency)}</Typography>
                        </Box>
                      ))}
                    </Stack>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }}>{t('groupBilling.invoice.total')}</Typography>
                      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }} data-testid="group-invoice-preview-total">
                        {formatMoney(draft.lines.reduce((s, l) => s + Number(l.amount), 0), currency)}
                      </Typography>
                    </Box>
                    {draft.installments?.length ? (
                      <Box sx={{ mt: 2 }}>
                        <Typography sx={eyebrowSx}>{t('groupBilling.invoice.schedule')}</Typography>
                        {draft.installments.map((inst) => (
                          <Box key={`${inst.labelKey}-${inst.dueDate}`} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                            <Typography sx={{ fontSize: 13 }}>
                              {t(`groupBilling.installment.${inst.labelKey}`, { defaultValue: inst.label })} · {inst.dueDate}
                            </Typography>
                            <Typography sx={{ fontSize: 13 }}>{formatMoney(inst.amount, currency)}</Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : null}
                  </>
                )}
              </Box>
            )}
          </Box>

          <Stack spacing={2}>
            <Typography sx={eyebrowSx}>{t('groupBilling.invoice.howPays')}</Typography>
            <CollectionMethodTiles value={method} onChange={setMethod} testIdPrefix="group-invoice-method" />
            {method === 'external_link' && (
              <>
                <TextField
                  label={t('groupBilling.invoice.linkUrl')}
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://"
                  size="small"
                  sx={fieldSx}
                  error={Boolean(linkUrl) && linkInvalid}
                  helperText={Boolean(linkUrl) && linkInvalid ? t('groupBilling.invoice.linkInvalid') : t('groupBilling.invoice.linkHint')}
                  inputProps={{ 'data-testid': 'group-invoice-link' }}
                />
                <TextField
                  label={t('groupBilling.invoice.linkLabel')}
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                  size="small"
                  sx={fieldSx}
                  inputProps={{ maxLength: 80 }}
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={applyToTrip}
                      onChange={(e) => setApplyToTrip(e.target.checked)}
                      inputProps={{ 'data-testid': 'group-invoice-apply-trip' }}
                    />
                  )}
                  label={t('groupBilling.invoice.applyToTrip')}
                />
              </>
            )}
            {method === 'offline' && (
              <TextField
                label={t('groupBilling.invoice.offlineInstructions')}
                value={offline}
                onChange={(e) => setOffline(e.target.value)}
                multiline
                minRows={3}
                size="small"
                sx={fieldSx}
                inputProps={{ 'data-testid': 'group-invoice-offline' }}
              />
            )}
            {method === 'in_app' && (
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>{t('groupBilling.invoice.inAppHint')}</Typography>
            )}
            <FormControlLabel
              control={<Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />}
              label={account.email
                ? t('groupBilling.invoice.emailTo', { email: account.email })
                : t('groupBilling.invoice.emailPayer')}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${DOCUMENT.rule}` }}>
        <Button onClick={onClose} sx={partnerLinkSx}>{t('common.cancel')}</Button>
        <Button
          onClick={submit}
          disabled={saving || linkInvalid}
          sx={partnerOxbloodBtnSx}
          data-testid="group-invoice-submit"
        >
          {saving ? <CircularProgress size={20} sx={{ color: DOCUMENT.ivory }} /> : t(`groupBilling.invoice.${mode}Action`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupSendInvoiceDialog;
